import React, { useCallback, useEffect, useState, useRef } from 'react';

import * as Sentry from '@sentry/nextjs';
import { useRouter } from 'next/router';
import PropTypes from 'prop-types';
import { useSelector, useDispatch } from 'react-redux';
import Sticky from 'react-stickynode';
import scrollIntoView from 'scroll-into-view-if-needed';
import styled, { css } from 'styled-components';

import {
  loadProfile,
  useCustomer,
  useMembership,
} from '@techstyle/react-accounts';
import { useFeature } from '@techstyle/react-features';
import { FormattedMessage } from '@techstyle/react-intl';
import {
  getNewDateFunction,
  updateSessionDetail,
  useSession,
} from '@techstyle/redux-core';

import { toggleCartDrawer } from '../../actions/cart';
import {
  addMembershipToCart,
  addProductSetToCart,
  loadCart,
} from '../../actions/checkout';
import {
  trackClickSizeSelection,
  trackMembershipCta,
  trackProductSetDetailsClose,
  trackProductSetDetailsOpen,
  trackProductSetReviewsClose,
  trackProductSetReviewsOpen,
} from '../../actions/products';
import {
  MembershipActionLocation,
  MembershipEventAction,
} from '../../constants/checkout';
import { desktop } from '../../styles';
import { BasicModal } from '../../styles/modal';
import { Paragraph } from '../../styles/paragraph';
import { localStorage } from '../../utils/helpers';
import { useLDFlags } from '../../utils/LD/useLDFlags';
import { useLDNewShoppingBagUX } from '../../utils/LD/useNewShoppingBagUX';
import {
  getDefaultSize,
  getDefaultSetsSelections,
  getDefaultSelectedPrice,
  findMatchingSize,
  findBraSizeCategory,
  hasLinkedProduct,
  getProductCategoryKey,
  getProductCategoryTypes,
} from '../../utils/productDetail';
import { getAvailableQuantitySize } from '../../utils/productQuantity';
import {
  allowExclusiveCheckout as allowExclusiveCheckoutSelector,
  getInfluencerItemCountExcludingMembership,
  getMembershipCartItem,
  getProductPromos,
  getShouldShowPromoPrice,
  isExclusiveProduct,
  isHiddenFromGrid as isHiddenFromGridSelector,
  isPreorderable as isPreorderableSelector,
  isSoldOut as isSoldOutSelector,
  isUnbreakableSet as isUnbreakableSetSelector,
  isXtraVipBox as isXtraVipBoxSelector,
} from '../../utils/selectors';
import { hasBraSize } from '../../utils/sizes';
import usePrevious from '../../utils/usePrevious';
import { Component as AddedToBasketCountFlag } from '../AddedToBasketCountFlag';
import { Container as PreorderShipDate } from '../PreorderShipDate';
import { Component as ProductDetailCTAs } from '../ProductDetailCTAs';
import { Component as ProductDetailSetItem } from '../ProductDetailSetItem';
import { Component as ProductImage } from '../ProductImage';
import { Component as SportPdpIndicator } from '../SportPdpIndicator';

const Wrapper = styled.div``;

const dialogStyle = css`
  ${desktop`
    border: ${props => props.theme.spacing.moderate}px solid ${props =>
    props.theme.colors.white};
  `};
`;

const StyledModal = styled(BasicModal)``;

const ModalProductImage = styled(ProductImage)`
  display: block;
`;

const NoPromoInfluencer = styled.div`
  text-align: center;
  padding: ${props => props.theme.spacing.small}px
    ${props => props.theme.spacing.medium}px;
  margin-bottom: ${props => props.theme.spacing.small}px;
  border-bottom-left-radius: ${props => props.theme.spacing.tiny}px;
  border-bottom-right-radius: ${props => props.theme.spacing.tiny}px;
  border: solid 1px ${props => props.theme.colors.lavender250};
`;

const NoPromoInfluencerTitle = styled.div`
  font-size: 14px;
  font-weight: 500;
  letter-spacing: 0.3px;
  margin-bottom: ${props => props.theme.spacing.tiny}px;
`;

const NoPromoInfluencerText = styled(Paragraph)`
  font-size: 12px;
  font-weight: 300;
  line-height: 1.33;
  letter-spacing: 0.3px;
  text-align: center;
`;

const TotalItem = styled.span`
  font-weight: bold;
`;

const StyledPreorderShipDate = styled(PreorderShipDate)`
  color: ${props => props.theme.colors.error};
  font-size: 14px;
  font-weight: 700;
  line-height: 21px;
  letter-spacing: 0.3px;
  margin-top: ${props => props.theme.spacing.moderate}px;
`;

function ProductSetDetailOptions({
  className,
  customText = '',
  customTextError,
  customToggleRef = {},
  handleHideRegCta,
  hideRegCta,
  onSetHeaderPositionchange,
  pSource,
  product,
  renderAddToBagError,
  shouldCheckCustomTextErr = false,
  showToastMessage,
  topItemHeaderPosition = 0,
}) {
  const sizeRef = useRef(new Map());
  const reviewRef = new Map();
  const sizeCache = JSON.parse(localStorage.getItem('selectedSize'));
  const dispatch = useDispatch();
  const router = useRouter();
  const hasSessionBeenInitialized = useRef(false);
  const shoppingBagFlagStatus = useLDNewShoppingBagUX();

  const { 'added-to-basket-flag': addedToBasketFlagEnabled } = useLDFlags();
  const { isEnabled: shouldOnlyShowSportsForVIP } = useFeature('sport_unlock');
  const {
    master_product_id: masterProductId,
    product_category_id_list: productCategoryIdListMaster,
    tag_id_list: tagIdListMaster,
  } = product;
  const { isSport } = getProductCategoryTypes(
    productCategoryIdListMaster,
    tagIdListMaster
  );

  const {
    allowExclusiveCheckout,
    allowOutOfStockCheckout,
    cart,
    cartItemInfluencerCount,
    deselectedMembershipCheckout,
    getNewDate,
    isBorderfreeCustomer,
    isExclusive,
    isHiddenFromGrid,
    isPreorderable,
    isSoldOut,
    isUnbreakableSet,
    isXtraVipBox,
    membershipItem,
    promos,
    reviews,
    shouldShowPromoPrice,
  } = useSelector(state => {
    return {
      allowExclusiveCheckout: allowExclusiveCheckoutSelector(state),
      allowOutOfStockCheckout: state.debug.allowOutOfStockCheckout,
      cart: state.checkout,
      cartItemInfluencerCount: getInfluencerItemCountExcludingMembership(state),
      deselectedMembershipCheckout: state.checkout.deselectedMembershipCheckout,
      getNewDate: getNewDateFunction(state),
      isBorderfreeCustomer: state.borderfree.isBorderfreeCustomer,
      isExclusive: isExclusiveProduct(state, { product }),
      isHiddenFromGrid: isHiddenFromGridSelector(state, { product }),
      isPreorderable: isPreorderableSelector(state, { product }),
      isSoldOut: isSoldOutSelector(state, { product }),
      isUnbreakableSet: isUnbreakableSetSelector(state, { product }),
      isXtraVipBox: isXtraVipBoxSelector(state, { product }),
      membershipItem: getMembershipCartItem(state),
      promos: getProductPromos(state, { product }),
      reviews: state.reviews.byMasterProductId,
      shouldShowPromoPrice: getShouldShowPromoPrice(state, { product }),
    };
  });

  const { dmGatewayCode: dmgCode, isServerOnly } = useSession();
  const { isVip, networkStatus } = useMembership();
  const membershipResolved = !isServerOnly && networkStatus.isUpToDate;
  const profile = useCustomer();
  const { influencerInfo: influencerPromoInfo, isInfluencer } = profile;

  const defaultSelectionSizes = getDefaultSetsSelections(
    product,
    profile,
    sizeCache,
    getNewDate
  );

  const [currentMasterProductID, setCurrentMasterProductID] =
    useState(masterProductId);
  const [selections, setSelections] = useState(defaultSelectionSizes);
  const [selectedPrice, setSelectedPrice] = useState(
    getDefaultSelectedPrice({ isVip, dmgCode, isBorderfreeCustomer })
  );
  const [selectedSizeList, setSelectedSizeList] = useState(
    defaultSelectionSizes
  );

  const [isItemHeaderStickyFixed, setIsItemHeaderStickyFixed] = useState({});

  const [selectedProductID, setSelectedProductID] = useState(null);
  const [cartButtonDisabled, setCartButtonDisabled] = useState(false);
  const [shouldShowImageModal, setShouldShowImageModal] = useState(false);
  const [imageModalProduct, setImageModalProduct] = useState(null);

  const hasNoInfluencerPromo =
    isInfluencer && !influencerPromoInfo.userHasActivePromo;

  const previousSelections = usePrevious(selections);
  const previousIsBorderfreeCustomer = usePrevious(isBorderfreeCustomer);
  const previousDmgCode = usePrevious(dmgCode);
  const previousIsVip = usePrevious(isVip);
  const previousProduct = usePrevious(product);
  const previousProfile = usePrevious(profile);

  // This is very important, when you search, the selections isn't changed
  // So what happens is, it gets errored because the selections and product-list isn't compatible
  if (masterProductId !== currentMasterProductID) {
    setCurrentMasterProductID(masterProductId);
    setSelections(defaultSelectionSizes);
  }

  // this is needed because the membershipItem was not being correctly loaded
  useEffect(() => {
    if (!membershipItem) {
      dispatch(loadCart());
    }
  }, [dispatch, membershipItem]);

  const handleColorChange = (event, color, id, isPreorderItem) => {
    const setItem = product.products_in_set.find(
      setItem => setItem.master_product_id === id
    );

    const currentSelection = selections[id];
    let newSelections = {};
    let band = null;
    let cup = null;

    const availableQuantitySize = getAvailableQuantitySize(
      color.sizes,
      currentSelection.size,
      isPreorderItem
    );

    if (
      (currentSelection.size.value != null && availableQuantitySize <= 0) ||
      currentSelection.size.value == null
    ) {
      newSelections = {
        ...selections,
        [id]: {
          ...selections[id],
          masterProductId: id,
          color: color,
          size: {
            sizeProduct: null,
            value: null,
            error: null,
            band,
            cup,
          },
        },
      };
    } else {
      const newSize = findMatchingSize(
        setItem,
        currentSelection.size,
        color.sizes,
        profile,
        isPreorderItem
      );

      const displayBraSize = hasBraSize(setItem);

      if (displayBraSize && newSize.value != null) {
        const braSizeCategorized = findBraSizeCategory(newSize.value);
        band = braSizeCategorized.band;
        cup = braSizeCategorized.cup;
      }

      newSelections = {
        ...selections,
        [id]: {
          ...selections[id],
          masterProductId: id,
          color: color,
          size: {
            ...newSize,
            band,
            cup,
          },
        },
      };
    }

    setSelections(newSelections);
  };

  const handleItemHeaderStickyState = (status, item) => {
    onSetHeaderPositionchange();

    if (
      status.status === Sticky.STATUS_FIXED ||
      status.status === Sticky.STATUS_RELEASED
    ) {
      setIsItemHeaderStickyFixed({
        isItemHeaderStickyFixed: {
          ...isItemHeaderStickyFixed,
          [item]: true,
        },
      });
    } else {
      setIsItemHeaderStickyFixed({
        isItemHeaderStickyFixed: {
          ...isItemHeaderStickyFixed,
          [item]: false,
        },
      });
    }
  };

  const setSizeCache = (size, productType) => {
    const selectedSizeCache =
      JSON.parse(localStorage.getItem('selectedSize')) || {};
    selectedSizeCache[productType] = size;
    localStorage.setItem('selectedSize', JSON.stringify(selectedSizeCache));
  };

  const handleSizeChange = (option, id, field) => {
    setSelectedProductID(id);
    const currentSelection = { ...selections[id] };
    let band = null;
    let cup = null;

    if (field === 'bandSize') {
      setSelections({
        ...selections,
        [id]: {
          ...currentSelection,
          size: {
            sizeProduct: null,
            value: null,
            error: null,
            band: option,
            cup,
          },
        },
      });
      return;
      // if we set up cup only
    } else if (field === 'cupSize') {
      setSelections({
        ...selections,
        [id]: {
          ...currentSelection,
          size: {
            sizeProduct: null,
            value: null,
            error: null,
            band,
            cup: option,
          },
        },
      });
      return;
    }

    // Derive `sizeProduct` from `value`
    const sizeList = currentSelection.color.sizes;
    const newSizeProduct = sizeList.find(size =>
      option.isOneSize
        ? size.product_id === option.productId
        : size.size === option.value
    );

    // Size setting in local storage
    const productSelected = product.products_in_set.find(
      product => product.master_product_id === id
    );
    const {
      product_category_id_list: productCategoryIdList,
      tag_id_list: tagIdList,
    } = productSelected;

    const productType = getProductCategoryKey(productCategoryIdList, tagIdList);
    setSizeCache(option.value, productType);

    if (field === 'braSize') {
      // both band and cup are selected but combination exist or not
      let categorizeBraSize = null;
      if (newSizeProduct) {
        categorizeBraSize = findBraSizeCategory(newSizeProduct.size);
      } else {
        if (option.value) {
          categorizeBraSize = findBraSizeCategory(option.value);
        } else {
          categorizeBraSize = findBraSizeCategory(option);
        }
      }
      band = categorizeBraSize.band;
      cup = categorizeBraSize.cup;
    }

    if (!newSizeProduct) {
      // Reset to default
      setSelections({
        ...selections,
        [id]: {
          ...currentSelection,
          size: {
            sizeProduct: null,
            value: option != null ? option : null,
            error:
              option.value === '' || option === '' ? null : 'SIZE_UNAVAILABLE',
            band: band != null ? band : null,
            cup: cup != null ? cup : null,
          },
        },
      });
    } else {
      const newSize = {
        sizeProduct: newSizeProduct,
        value: option.value,
        error:
          option.isSoldOut && !allowOutOfStockCheckout
            ? 'SIZE_UNAVAILABLE'
            : null,
        band,
        cup,
      };

      setCartButtonDisabled(false);
      setSelections({
        ...selections,
        [id]: {
          ...currentSelection,
          size: newSize,
        },
      });

      dispatch(
        trackClickSizeSelection({
          productID: product.master_product_id,
          isSet: true,
          prodSize: newSize.sizeProduct.label_instance,
          productSetId: currentSelection.masterProductId,
        })
      );
    }
  };

  const scrollToTopReviews = id => {
    const node = reviewRef.get(id);

    if (node) {
      scrollIntoView(node, {
        behavior: 'smooth',
        scrollMode: 'always',
        block: 'center',
      });
    }
  };

  const updateReviewRef = id => node => {
    reviewRef.set(id, node);
  };

  const updateSizeRef = id => node => {
    sizeRef.current.set(id, node);
  };

  const renderOptionSelectors = () => {
    const isCombinedSet = hasLinkedProduct(product);
    const extraQueryParams = { psrc: pSource };

    return product.products_in_set.map((setItem, index) => (
      <ProductDetailSetItem
        key={setItem.master_product_id}
        extraQueryParams={extraQueryParams}
        handleColorChange={handleColorChange}
        handleItemHeaderStickyState={handleItemHeaderStickyState}
        handleSizeChange={handleSizeChange}
        index={index}
        isBorderfreeCustomer={isBorderfreeCustomer}
        isCombinedSet={isCombinedSet}
        isItemHeaderFixed={isItemHeaderStickyFixed[`item-${index}`]}
        isPreorderable={isPreorderable}
        isSoldOut={isSoldOut}
        isUnbreakableSet={isUnbreakableSet}
        isXtraVipBox={isXtraVipBox}
        reviews={reviews}
        scrollToTopReviews={scrollToTopReviews}
        selectedSizeList={selectedSizeList}
        selections={selections}
        setItem={setItem}
        showImageModal={showImageModal}
        topItemHeaderPosition={topItemHeaderPosition}
        trackProductSetDetailsClose={() =>
          dispatch(trackProductSetDetailsClose)
        }
        trackProductSetDetailsOpen={() => dispatch(trackProductSetDetailsOpen)}
        trackProductSetReviewsClose={() =>
          dispatch(trackProductSetReviewsClose)
        }
        trackProductSetReviewsOpen={() => dispatch(trackProductSetReviewsOpen)}
        updateReviewRef={updateReviewRef}
        updateSizeRef={updateSizeRef}
      />
    ));
  };

  const checkInfluencerLimit = () => {
    const nItems = Object.keys(selections).length;
    const influencerLimit = profile.influencerInfo.maxProductQty;

    return cartItemInfluencerCount + nItems > influencerLimit;
  };

  const handleInfluencerError = () => {
    const influencerLimit = profile.influencerInfo.maxProductQty;
    const total = `${influencerLimit}/${influencerLimit}`;

    showToastMessage(
      <FormattedMessage
        id="site_pdp.influencer_limit"
        defaultMessage="You've got {total} items in your cart. Check out now."
        values={{
          total: <TotalItem>{total}</TotalItem>,
        }}
      />
    );
  };

  const checkSizes = () => {
    let updatedSelections = selections;
    const itemsWithErrors = [];
    const productItems = Object.keys(selections).map(key => selections[key]);

    productItems.forEach(item => {
      const { masterProductId, size } = item;
      let updatedItem = item;
      let availableQuantity = 0;

      if (size.sizeProduct) {
        availableQuantity = item.isPreorderItem
          ? size.sizeProduct.available_quantity_preorder
          : size.sizeProduct.available_quantity;
      }

      const isSizeSoldOut = availableQuantity <= 0;

      const isMissingSize = !size.value || !size.sizeProduct;

      let error = null;

      if (isMissingSize) {
        error = 'SIZE_REQUIRED';
      } else if (isSizeSoldOut && !allowOutOfStockCheckout) {
        error = 'SIZE_UNAVAILABLE';
      }

      if (error !== size.error) {
        updatedItem = {
          ...updatedItem,
          size: { ...size, error },
        };
        updatedSelections = {
          ...updatedSelections,
          [masterProductId]: updatedItem,
        };
      }

      if (updatedItem.size.error) {
        itemsWithErrors.push(updatedItem);
      }
    });

    if (updatedSelections !== selections) {
      setSelections(updatedSelections);
    }

    return itemsWithErrors;
  };

  const scrollToSelectSize = id => {
    const node = sizeRef.current.get(id);

    if (node) {
      scrollIntoView(node, {
        behavior: 'smooth',
        scrollMode: 'always',
        block: 'center',
      });
    }
  };

  const handleItemErrors = itemsWithErrors => {
    const firstItem = itemsWithErrors[0];
    scrollToSelectSize(firstItem.masterProductId);

    if (firstItem.size.error === 'SIZE_REQUIRED') {
      showToastMessage(
        <FormattedMessage
          id="site_pdp.size_required_error_message"
          defaultMessage="Select a size"
        />
      );
    } else if (firstItem.size.error === 'SIZE_UNAVAILABLE') {
      showToastMessage(
        <FormattedMessage
          id="site_pdp.size_sold_out_error_message"
          defaultMessage="Your selected size is sold out. Select another size."
        />
      );
    }
  };

  const renderCustomTextError = customTextError => {
    const node = customToggleRef.current;

    if (node) {
      scrollIntoView(node, {
        behavior: 'smooth',
        scrollMode: 'always',
        block: 'center',
      });
    }

    switch (customTextError) {
      case 'ERROR_EMPTY_VALUE':
        showToastMessage(
          <FormattedMessage
            id="site_pdp.custom_text_empty_value_error"
            defaultMessage="Enter your name"
          />
        );
        break;
      case 'ERROR_FORBIDDEN_CHARACTER':
        showToastMessage(
          <FormattedMessage
            id="site_pdp.custom_text_forbidden_character_error"
            defaultMessage="Contains forbidden characters"
          />
        );
        break;
      case 'ERROR_INVALID_LENGTH':
        showToastMessage(
          <FormattedMessage
            id="site_pdp.custom_text_invalid_length_error"
            defaultMessage="10 characters maximum"
          />
        );
        break;
      default:
        showToastMessage(
          <FormattedMessage
            id="site_pdp.custom_text_empty_value_error"
            defaultMessage="Enter your name"
          />
        );
        break;
    }
  };

  const addToCart = async option => {
    setCartButtonDisabled(true);

    // Open Cart Drawer immediately and show loading state in Drawer
    dispatch(toggleCartDrawer(true));
    dispatch(updateSessionDetail({ name: 'sxf_minicart', value: '' }));

    // we can put out of stock item in the cart - temporary decision

    const tasks = [];

    const pSource = router.query.psrc;
    const productsToAdd = Object.keys(selections)
      .map(id => selections[id].size)
      .map(size => size.sizeProduct);

    const shouldAddMembership =
      (option === 'vip' && !isVip && !isInfluencer && !membershipItem) ||
      (selectedPrice === 'defaultPrice' &&
        !isVip &&
        !isInfluencer &&
        !membershipItem &&
        !deselectedMembershipCheckout &&
        option !== 'guest');

    // find the product based on the selectedSize.value
    tasks.push(() =>
      dispatch(
        addProductSetToCart(
          product.master_product_id,
          productsToAdd.map(product => product.product_id),
          1,
          // TODO: Name this something not tied to membership.
          MembershipActionLocation.PDP,
          pSource,
          customText
        )
      )
    );

    if (shouldAddMembership) {
      tasks.push(() =>
        dispatch(
          addMembershipToCart({
            location: MembershipActionLocation.PDP,
            eventAction: MembershipEventAction.AUTO_ADD,
          })
        )
      );
    }

    // Run tasks serially so that parallel responses don't overwrite each other
    // in a nondeterministic way.
    while (tasks.length) {
      const task = tasks.shift();
      try {
        await task();
        if (shoppingBagFlagStatus?.isControl) {
          showToastMessage(
            <FormattedMessage
              id="global_checkout.cart_item_added_success_message"
              defaultMessage="Item added"
            />
          );
        }
      } catch (err) {
        if (err && err.errorCode) {
          renderAddToBagError(err.errorCode);
        }
        Sentry.captureException(err);
        if (shoppingBagFlagStatus?.isVariant) {
          const toastMessage = (
            <FormattedMessage
              id="global_checkout.something_went_wrong"
              defaultMessage="Something went wrong. Please try again."
            />
          );
          showToastMessage(toastMessage, 6000, true);
        }
      }
    }

    setCartButtonDisabled(false);

    if (shouldAddMembership) {
      handleHideRegCta(false);
    }
  };

  const handleAddToCart = option => {
    // Check for errors first before adding to cart
    let hasReachLimit = false;
    if (profile.isInfluencer) {
      hasReachLimit = checkInfluencerLimit();
    }
    if (hasReachLimit) {
      handleInfluencerError();
    } else {
      const itemsWithErrors = checkSizes();
      if (itemsWithErrors.length) {
        handleItemErrors(itemsWithErrors);
      } else if (shouldCheckCustomTextErr && (!customText || customTextError)) {
        renderCustomTextError(customTextError);
      } else {
        addToCart(option);
      }
    }
  };

  const handleRegCtaAddToCart = () => {
    handleAddToCart();
    dispatch(
      updateSessionDetail({
        name: 'add_to_cart_count',
        value: '',
      })
    );
  };

  const handleToggleButtonClick = option => {
    handleAddToCart(option);
    dispatch(
      updateSessionDetail({
        name: 'add_to_cart_count',
        value: option,
      })
    );
    dispatch(trackMembershipCta(option));
  };

  const showImageModal = product => {
    setShouldShowImageModal(true);
    setImageModalProduct(product);
  };

  const closeImageModal = () => {
    setShouldShowImageModal(false);
    setImageModalProduct(null);
  };

  const updateOnProductChange = useCallback(
    ({ product, profile }) => {
      // Clear out state if navigating from product set to single product (or vice versa)
      // Find matching size if changing colors
      const sizeCache = JSON.parse(localStorage.getItem('selectedSize'));
      const defaultSelections = getDefaultSetsSelections(
        product,
        profile,
        sizeCache,
        getNewDate
      );
      setSelections(defaultSelections);
      // Reset the sizeRef map
      sizeRef.current = new Map();
    },
    [getNewDate]
  );

  const updateOnProfileChange = useCallback(
    ({ product: masterProduct, profile }) => {
      // Update size dropdown with default size set in profile
      const newSelections = {};

      Object.keys(previousSelections || {}).forEach(id => {
        const currentSelection = previousSelections[id];
        const size = previousSelections[id].size;

        const product = masterProduct.products_in_set.find(setItem => {
          return setItem.master_product_id === parseInt(id, 10);
        });

        const newSize =
          size.sizeProduct === null ? getDefaultSize(product, profile) : size;

        let error = null;
        if (newSize.sizeProduct) {
          error =
            newSize.sizeProduct.available_quantity <= 0
              ? 'SIZE_UNAVAILABLE'
              : null;
        }
        newSelections[id] = {
          ...currentSelection,
          size: {
            ...newSize,
            error,
          },
        };
      });

      setSelections(newSelections);
    },
    [previousSelections, setSelections]
  );

  useEffect(() => {
    dispatch(loadProfile());

    // Set Session if no size is set in a specific category of profile
    const { products_in_set: productsInSet } = product;
    const shouldTriggerSession = productsInSet.some(product => {
      const {
        product_category_id_list: productCategoryIdList,
        tag_id_list: tagIdList,
      } = product;

      const productType = getProductCategoryKey(
        productCategoryIdList,
        tagIdList
      );

      const { profile: userProfile } = profile;
      const isCategoryProfileExist = userProfile && userProfile[productType];

      return !isCategoryProfileExist;
    });

    if (shouldTriggerSession && !hasSessionBeenInitialized.current) {
      hasSessionBeenInitialized.current = true;

      dispatch(
        updateSessionDetail({
          name: 'cache_size_eligibility',
          value: '',
        })
      );
    }
  });

  useEffect(() => {
    const selectedSizeProduct = selections[selectedProductID];

    if (
      previousSelections !== selections &&
      selectedSizeProduct &&
      selectedSizeProduct.size.value
    ) {
      setSelectedSizeList(selections);
    }

    // maybe move this part to useLayoutEffect
    if (
      isBorderfreeCustomer !== previousIsBorderfreeCustomer &&
      dmgCode !== previousDmgCode &&
      isVip !== previousIsVip
    ) {
      setSelectedPrice(
        getDefaultSelectedPrice({ isVip, dmgCode, isBorderfreeCustomer })
      );
    }

    if (product !== previousProduct) {
      updateOnProductChange({ product, profile });
    }

    if (profile && previousProfile && profile !== previousProfile) {
      updateOnProfileChange({ product, profile });
    }
  }, [
    selections,
    selectedProductID,
    previousSelections,
    setSelectedSizeList,
    isBorderfreeCustomer,
    dmgCode,
    isVip,
    setSelectedPrice,
    previousIsBorderfreeCustomer,
    previousDmgCode,
    previousIsVip,
    product,
    profile,
    previousProduct,
    previousProfile,
    updateOnProductChange,
    updateOnProfileChange,
  ]);

  if (
    !product ||
    !isItemHeaderStickyFixed ||
    !selections ||
    !selectedSizeList
  ) {
    return null;
  }

  const shouldNotShowCtaForSports = isSport && !shouldOnlyShowSportsForVIP;

  return (
    <Wrapper className={className}>
      {renderOptionSelectors()}
      <StyledPreorderShipDate product={product} />
      {addedToBasketFlagEnabled ? (
        <AddedToBasketCountFlag product={product} />
      ) : null}

      {shouldNotShowCtaForSports ? <SportPdpIndicator /> : null}

      <ProductDetailCTAs
        allowExclusiveCheckout={allowExclusiveCheckout}
        cart={cart}
        handleRegCtaAddToCart={handleRegCtaAddToCart}
        handleToggleButtonClick={handleToggleButtonClick}
        hideRegCta={hideRegCta}
        isCartButtonDisabled={cartButtonDisabled}
        isExclusive={isExclusive}
        isHiddenFromGrid={isHiddenFromGrid}
        isSoldOut={isSoldOut}
        isXtraVipBox={isXtraVipBox}
        isPreorderable={isPreorderable}
        isProductSet
        membershipResolved={membershipResolved}
        membershipItem={membershipItem}
        product={product}
        promos={promos}
        selections={selections}
        shouldNotShowCtaForSports={shouldNotShowCtaForSports}
        shouldShowPromoPrice={shouldShowPromoPrice}
        showToastMessage={showToastMessage}
      />

      {hasNoInfluencerPromo && (
        <NoPromoInfluencer>
          <NoPromoInfluencerTitle>
            <FormattedMessage
              id="site_pdp.no_promo_influencer_title"
              defaultMessage="No Promo Available"
            />
          </NoPromoInfluencerTitle>
          <NoPromoInfluencerText>
            <FormattedMessage
              id="site_pdp.no_promo_influencer_text"
              defaultMessage="Sorry! You don’t have an active promo code, and this is your ambassador account. This item cannot be added to bag. Need some Savage new items? Email us: ambassadors@savagex.com"
            />
          </NoPromoInfluencerText>
        </NoPromoInfluencer>
      )}

      <StyledModal
        isOpen={shouldShowImageModal}
        onExit={closeImageModal}
        dialogStyle={dialogStyle}
        titleId="product-image-set-modal"
      >
        <Wrapper id="product-image-set-modal">
          <ModalProductImage
            product={imageModalProduct}
            angle="LAYDOWN"
            size={8}
          />
        </Wrapper>
      </StyledModal>
    </Wrapper>
  );
}

ProductSetDetailOptions.TotalItem = TotalItem;

ProductSetDetailOptions.propTypes = {
  /**
   * Whether checkout with exclusive products is allowed even when the user
   * has no membership.
   */
  className: PropTypes.string,
  customText: PropTypes.string,
  customTextError: PropTypes.string,
  customToggleRef: PropTypes.object,
  onSetHeaderPositionchange: PropTypes.func,
  /**
   * Whether this product is tagged as "Xclusive".
   */
  product: PropTypes.shape({
    master_product_id: PropTypes.number,
    product_category_id_list: PropTypes.arrayOf(PropTypes.number),
    product_id: PropTypes.number,
    /* Product Sets have nested products */
    products_in_set: PropTypes.arrayOf(
      PropTypes.shape({
        product: PropTypes.shape({
          master_product_id: PropTypes.number,
          /* Color Options for a specific product in the set */
          related_product_id_object_list: PropTypes.arrayOf(
            PropTypes.shape({
              /* Size options for a specific color of a specific product in the set */
              product_id_object_list: PropTypes.arrayOf(
                PropTypes.shape({
                  available_quantity: PropTypes.number,
                  label_instance: PropTypes.string,
                  size: PropTypes.string,
                })
              ),
            })
          ),
        }),
      })
    ),
    tag_id_list: PropTypes.arrayOf(PropTypes.number),
  }),
  renderAddToBagError: PropTypes.func.isRequired,
  showToastMessage: PropTypes.func.isRequired,
  hideRegCta: PropTypes.bool,
  handleHideRegCta: PropTypes.func,
  shouldCheckCustomTextErr: PropTypes.bool,
  topItemHeaderPosition: PropTypes.number,
  pSource: PropTypes.string,
};

export default ProductSetDetailOptions;
